import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DataListTableActionElement,
  DataListTableDataElement,
  DataListTableElement,
  DataListTableLinkDataElement,
  DLT_DATA_TYPES,
} from '@el-data-list-table';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';

import {
  DB_COLLECTION_NAMES,
  MODULES,
  PERMISSION_ACTIONS,
  ROOMS_REFERENCE_CATEGORIES,
  ROOMS_REFERENCE_CATEGORY_FIELD,
  ROOMS_REFERENCE_SECTION,
  ROOMS_STATUS,
} from '@shared/constants';
import {
  IIdentity,
  IReferenceCategoryResponse,
  IRooms,
} from '@shared/interfaces';
import { getModulePermission } from '@shared/utils';

import { CISService } from '../../../../services/cis.service';
import { DateTimeFormatService } from '../../../../services/date-time-format.service';
import { FlowControlService } from '../../../../services/flow-control.service';
import {
  ICreateAction,
  ICreateData,
  ISpeedDialButtons,
  PermissionFiltrationService,
} from '../../../../services/permission-filtration.service';
import { IdentitiesInternalService } from '../../../identities/services/identities.internal.service';
import { ModulesService } from '../../../modules/services/modules.service';
import {
  ReferenceCategoryService,
  ReferenceService,
} from '../../../references/services';
import { IActionButton } from '../../../shared/components/action-button/action-button.component';
import { getAllCISChipsList } from '../../data/cis.filters';
import { RoomsService } from '../../services/rooms.service';

const SPEED_DIAL = {
  ADD_NEW: 'ADD_NEW',
};

const ACTIONS = {
  MAKE_A_BOOKING: 'MAKE_A_BOOKING',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  RESTORE: 'RESTORE',
};

@Component({
  selector: 'app-rooms',
  templateUrl: './rooms.component.html',
  styleUrls: ['./rooms.component.scss'],
})
export class RoomsComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    route: ActivatedRoute,
    private flowControlService: FlowControlService,
    private translate: TranslateService,
    private permissionFiltrationService: PermissionFiltrationService,
    private cisService: CISService,
    private roomsService: RoomsService,
    private identityInternalService: IdentitiesInternalService,
    private moduleService: ModulesService,
    private dateTimeFormatService: DateTimeFormatService,
    private referenceService: ReferenceService,
    private referenceCategoryService: ReferenceCategoryService
  ) {
    this.flowControlService.updateRouteInfo(
      this.router.url.split('?')[0],
      route.snapshot.data
    );
  }

  allPossibleKeys = ['is_search', 'name', 'available_resources', 'status'];
  allActionButtons: ISpeedDialButtons[] = [];
  filteredActionButtons: IActionButton[] = [];

  roomsListArray: DataListTableElement[] = [];

  // query params
  start = 0;
  size = 10;
  totalCount = 0;

  isListLoading = true;
  private onDestroy$ = new Subject<void>();

  identities: IIdentity[] = [];
  isIdentitiesModuleEnabled: boolean;
  identitiesModulesSub: Subscription;

  resources: string[];
  referenceCategories: IReferenceCategoryResponse[];

  ngOnInit() {
    this.filterSpeedDialButtons();
    this.checkModulesEnabled();
    this.listenToReferences();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
    this.identitiesModulesSub.unsubscribe();
  }

  listenToReferences() {
    this.referenceCategoryService.allCategories.subscribe(async (data) => {
      if (data && data.length === 0) {
        await this.referenceCategoryService.getAllReferenceCategories();
      }
      if (
        this.referenceCategories?.length === 0 ||
        this.referenceCategories === undefined
      ) {
        this.referenceCategories = data;

        const categoriesId = this.referenceCategories.find((category) => {
          return (
            ROOMS_REFERENCE_SECTION.ROOMS.toUpperCase() ===
              category.section.toUpperCase() &&
            category?.name?.trim()?.toUpperCase() ===
              ROOMS_REFERENCE_CATEGORIES.RESOURCES.toUpperCase()
          );
        })?._id;

        if (categoriesId) {
          const availableResourcesId = this.referenceCategories.find(
            (category) =>
              ROOMS_REFERENCE_SECTION.ROOMS.toUpperCase() ===
                category.section.toUpperCase() &&
              category?.name?.trim()?.toUpperCase() ===
                ROOMS_REFERENCE_CATEGORIES.RESOURCES.toUpperCase()
          )?._id;

          if (availableResourcesId) {
            await this.referenceService
              .getReferences(availableResourcesId?.toString())
              .then((data) => {
                this.resources = [];
                data?.forEach((data) => {
                  data.reference?.forEach((ref) => {
                    if (
                      ref.field.name.toUpperCase() ===
                        ROOMS_REFERENCE_CATEGORY_FIELD.NAME.toUpperCase() &&
                      !data?.is_deleted
                    ) {
                      this.resources.push(data.reference[0].value.toString());
                      // this.isLoading = false;
                    } else {
                      // this.isLoading = false;
                    }
                  });
                });

                this.cisService.initializesCIS(
                  this.allPossibleKeys,
                  getAllCISChipsList(this.translate, this.resources),
                  this.onDestroy$.asObservable(),
                  () => this.handleSearchButtonClickEvent(),
                  DB_COLLECTION_NAMES.ROOMS
                );
              });
          }
        }
      }
    });
  }

  private handleSearchButtonClickEvent() {
    this.start = 0;
    this.size = 10;
    this.searchRooms();
  }
  filterSpeedDialButtons() {
    this.allActionButtons = [
      {
        data: {
          action: SPEED_DIAL.ADD_NEW,
          icon: 'add',
          tooltip: this.translate.instant('rooms.create.add-new-button'),
          id: 'btn-add-new-room',
        },
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.WRITE,
        ]),
      },
    ];
    this.filteredActionButtons =
      this.permissionFiltrationService.filterSpeedDialButtons(
        this.allActionButtons
      );
  }

  onClickAction(action: string) {
    this.isListLoading = true;
    if (action === SPEED_DIAL.ADD_NEW) {
      this.router.navigate(['/app/rooms/create']);
    }
  }

  private checkModulesEnabled() {
    this.identitiesModulesSub = this.moduleService
      .listenToAvailability([MODULES.IDENTITIES])
      .subscribe((change) => {
        this.isIdentitiesModuleEnabled = change;
      });
  }

  showMore(shouldShowMore: boolean): void {
    if (shouldShowMore) {
      this.start = this.roomsListArray.length;
      this.searchRooms(true);
    }
  }

  pageSizeChange(e: number) {
    if (e) {
      this.size = e;
    }
  }

  action(e: { action: string; data: IRooms; index: number }) {
    switch (e.action) {
      case ACTIONS.MAKE_A_BOOKING:
        this.router.navigate([
          `/app/rooms/${(e.data as IRooms)._id}/make-a-booking`,
        ]);
        break;

      case ACTIONS.EDIT:
        this.router.navigate([`/app/rooms/${(e.data as IRooms)._id}/edit`]);
        break;

      case ACTIONS.DELETE:
        this.onClickDelete(e.data as IRooms, e.index);
        break;

      case ACTIONS.RESTORE:
        this.onClickRestore(e.data as IRooms, e.index);
        break;

      default:
        break;
    }
  }

  onClickDelete(room: IRooms, index: number) {
    // TODO: To be removed
    this.change_loading_state(index, true);

    this.roomsService.deleteRoomById(room._id.toString()).subscribe({
      next: (res) => {
        this.roomsListArray[index].originalData.status =
          ROOMS_STATUS.DEACTIVATED;
        this.update_item(res.data, index);
        this.change_loading_state(index, false);
      },
      error: () => {
        this.change_loading_state(index, false);
      },
    });
  }

  onClickRestore(room: IRooms, index: number) {
    this.change_loading_state(index, true);

    this.roomsService.restoreRoomById(room._id.toString()).subscribe({
      next: (res) => {
        this.roomsListArray[index].originalData.status =
          ROOMS_STATUS.DEACTIVATED;
        this.update_item(res.data, index);
        this.change_loading_state(index, false);
      },
      error: () => {
        this.change_loading_state(index, false);
      },
    });
  }

  private change_loading_state(index: number, is_loading: boolean) {
    this.roomsListArray[index.valueOf()].loading = is_loading;
    this.roomsListArray[index.valueOf()].expanded = !is_loading;
  }

  searchRooms(replaceUrl = false) {
    this.isListLoading = true;

    const { httpParams, onFinishSearch } = this.cisService.getSearchParams(
      this.start,
      this.size
    );

    let usedCISChipsList = httpParams.keys();
    usedCISChipsList = usedCISChipsList.filter(
      (chip) => chip !== 'is_search' && chip !== 'start' && chip !== 'size'
    );

    this.cisService.setFrequentlyUsedChipsFor(
      DB_COLLECTION_NAMES.ROOMS,
      usedCISChipsList
    );

    this.roomsService.getAllRooms(httpParams).subscribe({
      next: (res) => {
        this.totalCount = res?.count;
        this.roomsListArray = this.start > 0 ? this.roomsListArray : [];

        res.data.forEach((workflow) => {
          const index = this.roomsListArray.length;
          const actionElements = this.get_action_elements(workflow);
          const dataElements = this.get_data_elements(workflow);

          this.roomsListArray.push({
            loading: false,
            expanded: false,
            data: dataElements,
            icon: 'chair',
            actions: actionElements,
            index,
            originalData: workflow,
          });
        });

        this.isListLoading = false;
        onFinishSearch({ replaceUrl });
      },
      error: () => {
        this.isListLoading = false;
      },
    });
  }

  private get_data_elements(
    room: IRooms,
    identities?: IIdentity[]
  ): DataListTableDataElement[] {
    const dataElements: ICreateData[] = [];

    let status;
    let color;
    if (room.status === ROOMS_STATUS.ACTIVATED) {
      status = this.translate.instant('rooms.dlt.activated');
      color = 'green';
    } else {
      status = this.translate.instant('rooms.dlt.deactivated');
      color = 'red';
    }

    const createdByUser = identities?.find(
      (item) => item._id?.toString() === room.created_by.toString()
    );
    const createdByUserName = createdByUser
      ? `${createdByUser.salutation} ${createdByUser.first_name} ${
          createdByUser?.last_name || ''
        }`
      : `-`;

    dataElements.push(
      {
        data: {
          key: this.translate.instant('rooms.dlt.name'),
          value: room.name,
        },
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.status'),
          value: status,
          type: DLT_DATA_TYPES.CHIP,
          chipBackgroundColor: color,
          chipColor: 'white',
        },
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.seating-capacity'),
          value: room.seating_capacity,
          type: DLT_DATA_TYPES.PLAIN,
        },

        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.available-amenities'),
          value:
            room.available_resources.length > 0
              ? room.available_resources.join(', ')
              : '-',
          type: DLT_DATA_TYPES.PLAIN,
        },

        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.description'),
          value: room.description ?? '-',
          type: DLT_DATA_TYPES.PLAIN,
        },

        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          type: DLT_DATA_TYPES.SEPARATOR,
        },
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.created-by'),
          value: createdByUserName,
          type: this.isIdentitiesModuleEnabled
            ? DLT_DATA_TYPES.LINK
            : DLT_DATA_TYPES.PLAIN,
          href: '/app/identities',
          hrefParams: {
            is_search: true,
            _id: room.created_by.toString(),
            username: createdByUser?.username,
          },
          linkType: 'internal',
          lazy: !createdByUser,
          loading: !createdByUser,
        } as DataListTableLinkDataElement,
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.created-date-time'),
          value: this.dateTimeFormatService.formatDateTime(room.created_on),
        },
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      },
      {
        data: {
          key: this.translate.instant('rooms.dlt.last-updated-date-time'),
          value: this.dateTimeFormatService.formatDateTime(
            room.last_modified_on
          ),
        },
        allowedPermissions: getModulePermission(MODULES.ROOMS, [
          PERMISSION_ACTIONS.READ,
        ]),
      }
    );

    return this.permissionFiltrationService.getData(dataElements);
  }

  private get_action_elements(room: IRooms): DataListTableActionElement[] {
    const actionElements: ICreateAction[] = [];
    switch (room?.is_deleted) {
      case true:
        actionElements.push(this.create_restore_action(room));
        break;
      case false:
        actionElements.push(this.create_make_a_booking_action(room));
        actionElements.push(this.create_edit_action(room));
        actionElements.push(this.create_delete_action(room));
        break;
    }

    return this.permissionFiltrationService.getActions(actionElements);
  }

  private create_make_a_booking_action(room: IRooms): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'work',
      face: ' ' + this.translate.instant('rooms.dlt.faces.make-a-booking'),
      tooltip: this.translate.instant('rooms.dlt.tooltip.make-a-booking'),
      action: ACTIONS.MAKE_A_BOOKING,
      data: room,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.ROOMS, [
        PERMISSION_ACTIONS.READ,
      ]),
    };
  }

  private create_edit_action(room: IRooms): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'edit',
      face: '',
      tooltip: this.translate.instant('rooms.dlt.tooltip.edit'),
      action: ACTIONS.EDIT,
      data: room,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.ROOMS, [
        PERMISSION_ACTIONS.EDIT,
      ]),
    };
  }

  private create_delete_action(room: IRooms): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'delete',
      face: '',
      tooltip: this.translate.instant('rooms.dlt.tooltip.delete'),
      action: ACTIONS.DELETE,
      data: room,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.ROOMS, [
        PERMISSION_ACTIONS.DELETE,
      ]),
    };
  }

  private create_restore_action(room: IRooms): ICreateAction {
    const action: DataListTableActionElement = {
      icon: 'restore_from_trash',
      face: '',
      tooltip: this.translate.instant('rooms.dlt.tooltip.restore'),
      action: ACTIONS.RESTORE,
      data: room,
    };
    return {
      action,
      allowedPermissions: getModulePermission(MODULES.ROOMS, [
        PERMISSION_ACTIONS.RESTORE,
      ]),
    };
  }

  onExpand(index: number) {
    const workflow = this.roomsListArray[index].originalData;
    this.populateChangedBy(index, workflow);
  }

  async populateChangedBy(index: number, room: IRooms): Promise<void> {
    const ids: string[] = [];
    ids.push(room.created_by.toString());
    this.identityInternalService
      .getSetOfPublicIdentitiesFromConnection(ids)
      .subscribe({
        next: (res) => {
          this.identities.push(res.data);
        },
        error: () => {
          this.identities.push(undefined);
        },
        complete: async () => {
          this.roomsListArray[index].data = this.get_data_elements(
            room,
            this.identities
          );
        },
      });
  }

  private update_item(room: IRooms, index: number): void {
    const actionElements = this.get_action_elements(room);
    const dataElements = this.get_data_elements(room);

    this.roomsListArray[index].actions = actionElements;
    this.roomsListArray[index].data = dataElements;
    this.roomsListArray[index].originalData = room;
  }
}
